<template>
  <section id="page-foobar-editor">
    <div class="container">
      <div class="row">
        <clearfix :biggest="true"></clearfix>
      </div>

      <div class="row" v-show="loading">
        <div class="col-sm-12 content fb-bg-light">
          <loading-spinner></loading-spinner>
        </div>
      </div>

      <div class="row" v-show="!loading">
        <div class="col-sm-12 content fb-bg-dark">
          <h3 v-show="!editTitle" :class="editableClasses" @click="startTitleEdit">{{ foobar.title }}</h3>
          <div v-show="editTitle">
            <label for="foobar-title">{{ $t('message.field.title') }}</label>
            <input id="foobar-title" type="text" class="full" v-model="foobar.title">
            <br>&nbsp;<br>
            <b-button :size="'sm'" :variant="'secondary'" @click="updateTitle">
              <icon name="plus-square"></icon>&nbsp;
              {{ $t('message.button.save') }}
            </b-button>
          </div>
        </div>

        <clearfix :wide="true"></clearfix>

        <div class="col-sm-12 content fb-bg-light">
          <div v-show="!editDescription" :class="editableClasses" class="data" @click="startDescriptionEdit"
               v-html="foobar.description"></div>
          <div v-show="editDescription">
            <label for="foobar-description">{{ $t('message.field.description') }}</label><br>
            <quill-editor id="foobar-description"
                          v-model="foobar.description"
                          @change="changeDescription"
                          :options="quillOptionsDescription"></quill-editor>
            <br>
            <b-button :size="'sm'" :variant="'secondary'" @click="updateDescription">
              <icon name="plus-square"></icon>&nbsp;
              {{ $t('message.button.save') }}
            </b-button>
          </div>
        </div>

        <clearfix :wide="true"></clearfix>

        <div class="col-sm-12 content fb-bg-light">
          <h4>{{ $t('message.field.md') }}</h4>
          <div :class="editableClasses" class="data" @click="startMarkdownEdit">
            <vue-markdown :source="foobar.md" lang-prefix="hljs "></vue-markdown>
          </div>
          <br v-show="editMarkdown">
          <div v-show="editMarkdown">
            <label for="foobar-md">{{ $t('message.field.md') }}</label><br>
            <b-textarea :rows="10" id="foobar-md" class="full" v-model="foobar.md"
                        :placeholder="$t('message.form.placeholder.md')"></b-textarea>
            <br>
            <b-button :size="'sm'" :variant="'secondary'" @click="updateMarkdown">
              <icon name="plus-square"></icon>&nbsp;
              {{ $t('message.button.save') }}
            </b-button>
          </div>
        </div>
      </div>


      <div class="row" v-show="!loading && foobar.snippets.length === 0">
        <clearfix :wide="true"></clearfix>
        <div class="col-sm-12 content fb-bg-light">
          {{ $t('message.text.no_snippets_found') }}
        </div>
      </div>

      <div class="row" v-show="!loading && foobar.snippets.length > 0" v-for="snippet in foobar.snippets">
        <clearfix :wide="true"></clearfix>
        <div class="col-sm-12 content fb-bg-light">
          <div class="foobar-edit-menu single-control" :title="$t('message.button.delete')"
               v-on:click="deleteSnippet(snippet.id)">
            <icon name="minus-circle"></icon>
          </div>

          <a :name="snippet.id"></a>
          <h3 :class="editableClasses" class="data" v-show="!editSnippet[snippet.id].title"
              @click="startSnippetTitleEdit(snippet.id)">{{ snippet.title }}</h3>
          <div v-show="editSnippet[snippet.id].title">
            <input type="text" v-model="snippet.title" class="full">
            <br>&nbsp;<br>
            <b-button :size="'sm'" :variant="'secondary'" @click="updateSnippetTitle(snippet.id)">
              <icon name="plus-square"></icon>&nbsp;
              {{ $t('message.button.save') }}
            </b-button>
            <br>&nbsp;<br>
          </div>
          <div :class="editableClasses" class="data" v-show="!editSnippet[snippet.id].content"
               @click="startSnippetContentEdit(snippet.id)">
            <pre v-highlightjs="snippet.content"><code :class="snippet.lang"></code></pre>
            <div class="snippet-id">{{ snippet.id }}</div>
          </div>
          <div v-show="editSnippet[snippet.id].content">
            <b-textarea :rows="10" v-model="snippet.content"></b-textarea>
            <br>
            <b-select v-model="snippet.lang" :options="langTypes"></b-select>
            <br>&nbsp;<br>
            <b-button :size="'sm'" :variant="'secondary'" @click="updateSnippetContent(snippet.id)">
              <icon name="plus-square"></icon>&nbsp;
              {{ $t('message.button.save') }}
            </b-button>
          </div>
        </div>
      </div>

      <clearfix :wide="true"></clearfix>

      <div class="row" v-show="!loading">
        <div class="col-sm-12 content fb-bg-dark">
          <b-button :size="'sm'" :variant="'primary'" @click="addSnippet()">
            <icon name="plus-square"></icon>&nbsp;
            {{ $t('message.button.add_snippet') }}
          </b-button>
          <b-button :size="'sm'" :variant="'primary'" @click="editOrder()">
            <icon name="sort"></icon>&nbsp;
            {{ $t('message.button.edit_snippet_order') }}
          </b-button>
        </div>
      </div>

      <clearfix :wide="true"></clearfix>

      <div class="row" v-show="!loading" >
        <clearfix :wide="true"></clearfix>

        <div class="col-sm-12 content fb-bg-light">
          <div>
            <span>{{ $t('message.title.tags') }}:&nbsp;</span><b-button :size="'sm'" :variant="'secondary'" v-for="tag in foobar.tags" class="fb-tag" @click="deleteTag(tag)">{{ tag }}</b-button>
            <input v-model="newTag">&nbsp;<b-button :size="'sm'" :variant="'secondary'" @click="addTag()">{{ $t('message.button.add') }}</b-button>
          </div>
        </div>
      </div>

      <clearfix :biggest="true"></clearfix>

      <modal v-if="showOrderSnippets" @close="showOrderSnippets = false">
        <h3 slot="header">{{ $t('message.button.edit_snippet_order') }}</h3>

        <div slot="body">
          <draggable v-model="foobar.snippets">
            <div v-for="snippet in foobar.snippets" :key="snippet.id">{{ snippet.title }}</div>
          </draggable>
        </div>

        <div slot="buttons">
          <b-button :size="'large'" :variant="'secondary'" class="active-item" @click="saveOrder"><icon name="check"></icon>&nbsp;{{ $t('message.button.save') }}</b-button>
        </div>
      </modal>
    </div>
  </section>
</template>

<script>
  import Vue from 'vue'
  import VueMarkdown from 'vue-markdown'
  import axios from 'axios'
  import VueAxios from 'vue-axios'
  import 'vue-awesome/icons'
  import Icon from 'vue-awesome/components/Icon.vue'
  import {quillEditor} from 'vue-quill-editor'
  import miniToastr from 'mini-toastr'
  import Clearfix from '@/components/utils/Clearfix'
  import LoadingSpinner from '@/components/utils/LoadingSpinner'
  import config from './Config'
  import Modal from './SnippetOrderModal'
  import draggable from 'vuedraggable'

  import _ from 'underscore'

  Vue.use(VueAxios, axios)

  export default {
    props: {
      foobarId: String
    },
    components: {
      Clearfix, LoadingSpinner, quillEditor, Icon, VueMarkdown, Modal, draggable
    },
    data() {
      return {
        newTag: '',
        foobar: {snippets: []},
        loading: true,

        langTypes: [
          {value: 'plaintext', text: 'Plaintext'},
          {value: 'bash', text: 'BASH'},
          {value: 'cpp', text: 'C++'},
          {value: 'clojure', text: 'Clojure'},
          {value: 'css', text: 'CSS'},
          {value: 'gherkin', text: 'Cucumber'},
          {value: 'http', text: 'HTTP'},
          {value: 'diff', text: 'Diff'},
          {value: 'dos', text: 'Dos .bat'},
          {value: 'go', text: 'Go'},
          {value: 'groovy', text: 'Groovy'},
          {value: 'http', text: 'HTTP'},
          {value: 'java', text: 'Java'},
          {value: 'javascript', text: 'JavaScript'},
          {value: 'json', text: 'JSON'},
          {value: 'kotlin', text: 'Kotlin'},
          {value: 'lisp', text: 'Lisp'},
          {value: 'makefile', text: 'Makefile'},
          {value: 'markdown', text: 'Markdown'},
          {value: 'perl', text: 'Perl'},
          {value: 'pgsql', text: 'PGSQL'},
          {value: 'php', text: 'PHP'},
          {value: 'python', text: 'Python'},
          {value: 'properties', text: 'Properties'},
          {value: 'ruby', text: 'Ruby'},
          {value: 'sql', text: 'SQL'},
          {value: 'vim', text: 'Vim Script'},
          {value: 'x86asm', text: 'X86 ASm'},
          {value: 'xml', text: 'XML'}
        ],

        quillOptionsDescription: {placeholder: this.$t('message.form.placeholder.description')},

        editDependency: false,
        editDescription: false,
        editSnippet: {},
        editOutput: false,
        editMarkdown: false,
        editTitle: false,

        showOrderSnippets: false
      }
    },
    computed: {
      editableClasses() {
        return {
          'editable-field': this.$store.state.auth.editor
        }
      }
    },
    methods: {
      startTitleEdit() {
        this.editTitle = this.$store.state.auth.editor
      },
      updateTitle() {
        Vue.axios.put(this.$t(config.urls.foobar_update_title, {foobar_id: this.foobarId}), {data: this.foobar.title})
          .then((response) => {
            miniToastr.success(this.$t('message.text.saved'))
            this.editTitle = false
          })
          .catch(() => {
            miniToastr.error(this.$t('message.error.saving_foobar_failed'))
          })
      },

      startDependencyEdit() {
        this.editDependency = this.$store.state.auth.editor
      },
      updateDependency() {
        Vue.axios.put(this.$t(config.urls.foobar_update_dependency, {foobar_id: this.foobarId}), {data: this.foobar.dependency})
          .then((response) => {
            miniToastr.success(this.$t('message.text.saved'))
            this.editDependency = false
          })
          .catch(() => {
            miniToastr.error(this.$t('message.error.saving_foobar_failed'))
          })
      },

      startSnippetTitleEdit(snippetId) {
        Vue.set(this.editSnippet, snippetId, {title: true, content: this.editSnippet[snippetId].content})
      },
      endSnippetTitleEdit(snippetId) {
        Vue.set(this.editSnippet, snippetId, {title: false, content: this.editSnippet[snippetId].content})
      },
      updateSnippetTitle(snippetId) {
        const snippet = _.find(this.foobar.snippets, (s) => {
          return s.id === snippetId
        })
        Vue.axios.put(this.$t(config.urls.foobar_update_snippet_title, {
          foobar_id: this.foobarId,
          snippet_id: snippetId
        }), {data: snippet.title})
          .then(() => {
            miniToastr.success(this.$t('message.text.saved'))
            this.endSnippetTitleEdit(snippetId)
          })
          .catch(() => {
            miniToastr.error(this.$t('message.error.saving_foobar_failed'))
          })
      },

      startSnippetContentEdit(snippetId) {
        Vue.set(this.editSnippet, snippetId, {title: this.editSnippet[snippetId].title, content: true})
      },
      endSnippetContentEdit(snippetId) {
        Vue.set(this.editSnippet, snippetId, {title: this.editSnippet[snippetId].title, content: false})
      },
      updateSnippetContent(snippetId) {
        const snippet = _.find(this.foobar.snippets, (s) => {
          return s.id === snippetId
        })
        Vue.axios.put(this.$t(config.urls.foobar_update_snippet, {foobar_id: this.foobarId}), snippet)
          .then(() => {
            miniToastr.success(this.$t('message.text.saved'))
            this.endSnippetContentEdit(snippetId)
          })
          .catch(() => {
            miniToastr.error(this.$t('message.error.saving_foobar_failed'))
          })
      },

      addSnippet() {
        Vue.axios.post(this.$t(config.urls.foobar_update_snippet, {foobar_id: this.foobarId}))
          .then(() => {
            miniToastr.success(this.$t('message.text.saved'))
            this.loadFoobar()
          })
          .catch(() => {
            miniToastr.error(this.$t('message.error.deleting_foobar_failed'))
          })
      },
      deleteSnippet(snippetId) {
        Vue.axios.delete(this.$t(config.urls.foobar_delete_snippet, {foobar_id: this.foobarId, snippet_id: snippetId}))
          .then(() => {
            miniToastr.success(this.$t('message.text.deleted'))
            this.loadFoobar()
          })
          .catch(() => {
            miniToastr.error(this.$t('message.error.deleting_foobar_failed'))
          })
      },
      editOrder() {
        this.showOrderSnippets = true
      },
      saveOrder() {
        Vue.axios.put(this.$t(config.urls.foobar_update_snippet_order, {foobar_id: this.foobarId}), {snippets: this.foobar.snippets})
          .then(() => {
            miniToastr.success(this.$t('message.text.saved'))
            this.showOrderSnippets = false
          })
          .catch(() => {
            miniToastr.error(this.$t('message.error.saving_foobar_failed'))
          })
      },

      updateSnippet() {
        Vue.axios.put(this.$t(config.urls.foobar_update_snippet, {foobar_id: this.foobarId}), {
          lang: this.foobar.snippet.lang,
          snippet: this.foobar.snippet.content
        })
          .then((response) => {
            miniToastr.success(this.$t('message.text.saved'))
            this.editSnippet = false
          })
          .catch(() => {
            miniToastr.error(this.$t('message.error.saving_foobar_failed'))
          })
      },

      deleteTag(tag) {
        Vue.axios.delete(this.$t(config.urls.foobar_add_tag, {foobar_id: this.foobarId, tag: tag}))
          .then((response) => {
            miniToastr.success(this.$t('message.text.saved'))
            this.loadFoobar()
          })
          .catch(() => {
            miniToastr.error(this.$t('message.error.saving_foobar_failed'))
          })
      },

      addTag() {
        Vue.axios.post(this.$t(config.urls.foobar_add_tag, {foobar_id: this.foobarId, tag: this.newTag}))
          .then((response) => {
            miniToastr.success(this.$t('message.text.saved'))
            this.loadFoobar()
            this.newTag = ''
          })
          .catch(() => {
            miniToastr.error(this.$t('message.error.saving_foobar_failed'))
          })
      },

      startOutputEdit() {
        this.editOutput = this.$store.state.auth.editor
      },
      updateOutput() {
        Vue.axios.put(this.$t(config.urls.foobar_update_output, {foobar_id: this.foobarId}), {data: this.foobar.output})
          .then((response) => {
            miniToastr.success(this.$t('message.text.saved'))
            this.editOutput = false
          })
          .catch(() => {
            miniToastr.error(this.$t('message.error.saving_foobar_failed'))
          })
      },

      startDescriptionEdit() {
        this.editDescription = this.$store.state.auth.editor
      },
      changeDescription({html}) {
        this.foobar.description = html
      },
      updateDescription() {
        Vue.axios.put(this.$t(config.urls.foobar_update_description, {foobar_id: this.foobarId}), {data: this.foobar.description})
          .then((response) => {
            miniToastr.success(this.$t('message.text.saved'))
            this.editDescription = false
          })
          .catch(() => {
            miniToastr.error(this.$t('message.error.saving_foobar_failed'))
          })
      },

      startMarkdownEdit() {
        this.editMarkdown = this.$store.state.auth.editor
      },
      updateMarkdown() {
        Vue.axios.put(this.$t(config.urls.foobar_update_md, {foobar_id: this.foobarId}), {data: this.foobar.md})
          .then((response) => {
            miniToastr.success(this.$t('message.text.saved'))
            this.editMarkdown = false
          })
          .catch(() => {
            miniToastr.error(this.$t('message.error.saving_foobar_failed'))
          })
      },
      loadFoobar() {
        Vue.axios.get(config.urls.foobar + this.foobarId)
          .then((response) => {
            this.foobar = response.data

            _.each(this.foobar.snippets, (snippet) => {
              Vue.set(this.editSnippet, snippet.id, {
                title: false,
                content: false
              })
            })
            this.loading = false
          })
      }
    },
    created() {
      this.loadFoobar()
    }
  }
</script>

<style>
  #page-foobar-editor {
    background: url('../../assets/img/bg/coding.jpg') center fixed no-repeat;
    background-size: cover;
    height: auto;
    margin: 0 auto;
    width: 100%;
    position: relative;
    padding: 30px 0;
  }
</style>



// WEBPACK FOOTER //
// FoobarEdit.vue?809c15a4